import { Component, OnInit, OnDestroy } from "@angular/core";
import { Subscription, Observable } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import {
  FormGroup,
  FormControl,
  Validators,
  FormBuilder,
  AbstractControl,
  FormArray,
} from "@angular/forms";
import { take, map, concatMap } from "rxjs/operators";
import { EjercicioFiscal } from "src/app/models/ejercicio-fiscal";
import { DomSanitizer } from "@angular/platform-browser";
import { CatalogoService } from "src/app/services/catalogo.service";
import { RutaCatalogoEnum } from "src/app/shared/enums/ruta-catalogo.enum";
import { ElementoCatalogo } from "src/app/models/entities/elemento-catalogo";
import { ObraDTO } from "src/app/models/dtos/obra-dto";
import { ObraService } from "src/app/services/obra.service";
import { Estado } from "src/app/models/entities/estado";
import { LevantamientoObra } from "src/app/models/entities/levantamiento-obra";
import { Obra } from "src/app/models/entities/obra";
import { LevantamientoObraService } from "src/app/services/levantamiento-obra.service";
import { FacadeSesionService } from "src/app/services/facade-sesion.service";
import { UsuarioSesionDTO } from "src/app/models/dtos/usuario-sesion-dto";
import { Usuario } from "src/app/models/entities/usuario";
import { AlertasService } from "src/app/services/alertas.service";
import { ObrasHistoricoService } from "src/app/services/obras-historico.service";
import { ObraHistorico } from "src/app/models/entities/obra-historico";
import { DateUtils } from "src/app/shared/utils/date-utils";
import { Alert } from "src/app/models/alert";

@Component({
  selector: "edicion-ficha-tecnica",
  templateUrl: "./edicion-ficha-tecnica.component.html",
  styleUrls: ["./edicion-ficha-tecnica.component.scss"],
})
export class EdicionFichaTecnicaComponent implements OnInit, OnDestroy {
  public fichaTecnicaForm: FormGroup;
  public usuarioEnSesion: Usuario;
  public keyObra: string;
  public datosObra: ObraDTO;
  public dataLoaded: boolean = false;
  public suscripcionObra: Subscription;
  public tiposCarpetas$: Observable<ElementoCatalogo[]>;
  public tiposSecciones$: Observable<ElementoCatalogo[]>;
  public catTipoPrioridad$: Observable<ElementoCatalogo[]>;
  public catTipoOrigenRecursos$: Observable<ElementoCatalogo[]>;
  public catTipoCorredor$: Observable<ElementoCatalogo[]>;
  public readonly fechaActual: number = new Date().getTime();
  public date: Date = new Date();
  public catalogoEstados: Estado[] = [];
  public idiomaCalendario: any;
  public anioActual: number = new Date().getFullYear() - 15;
  periodoInicio: Date;
  periodoFin: Date;
  alert: Alert = new Alert();

  public constructor(
    private formBuilder: FormBuilder,
    private obraService: ObraService,
    private obrasHistoricoService: ObrasHistoricoService,
    private levantamientoObraService: LevantamientoObraService,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private catalogoService: CatalogoService,
    private facadeSesionService: FacadeSesionService,
    private alertasService: AlertasService,
    private router: Router,
    private dateUtils: DateUtils
  ) {}

  public ngOnInit(): void {
    this.keyObra = this.activatedRoute.snapshot.params.keyObra;
    this.facadeSesionService.usuarioEnSesionService
      .obtenerUsuario()
      .subscribe(
        (usuarioSesionDTO: UsuarioSesionDTO) =>
          (this.usuarioEnSesion = usuarioSesionDTO.usuario)
      );
    this.obtenerCatalogos();
    this.obtenerFichaTecnica();
    this.obtenerCatalogoEstados();
    this.idiomaCalendario = this.dateUtils.idiomaEspanol;
  }
  public async obtenerCatalogoEstados(): Promise<any> {
    this.catalogoEstados = await this.catalogoService
      .obtenerCatalogo<Estado>(RutaCatalogoEnum.ESTADOS)
      .pipe(take(1))
      .toPromise();
  }

  public async obtenerFichaTecnica() {
    this.datosObra = await this.obraService
      .findByKey(this.keyObra)
      .pipe(
        concatMap((obra: Obra) =>
          this.levantamientoObraService
            .findByKey(obra.keyLevantamientoObra)
            .pipe(
              take(1),
              concatMap((levantamientoObra: LevantamientoObra) =>
                this.catalogoService
                  .obtenerCatalogo<Estado>(RutaCatalogoEnum.ESTADOS)
                  .pipe(
                    take(1),
                    map((estados: Estado[]) => {
                      let estado: Estado = estados.find(
                        (estado: Estado) =>
                          estado.key === levantamientoObra.keyCatEstado
                      );
                      return {
                        nombreEstado: estado.nombre,
                        nombreObra: levantamientoObra.nombreObra,
                        nombreTramoLicitacion:
                          levantamientoObra.nombreTramoLicitacion,
                        obra,
                      } as ObraDTO;
                    })
                  )
              )
            )
        ),
        take(1)
      )
      .toPromise();
    this.inicializarForm();
    this.dataLoaded = true;
    // Extraer en dos variables la propiedad periodo ejecución
    let string = this.datosObra.obra.periodoEjecucion;
    let string2: string[] = string.split(" ");
    this.periodoInicio = new Date(
      `${string2[1]}-${this.getMonth(string2[0])}-1`
    );
    this.periodoFin = new Date(`${string2[4]}-${this.getMonth(string2[3])}-1`);
  }

  getMonth(month: string): number {
    switch (month) {
      case "Enero":
        return 1;
      case "Febrero":
        return 2;
      case "Marzo":
        return 3;
      case "Abril":
        return 4;
      case "Mayo":
        return 5;
      case "Junio":
        return 6;
      case "Julio":
        return 7;
      case "Agosto":
        return 8;
      case "Septiembre":
        return 9;
      case "Octubre":
        return 10;
      case "Noviembre":
        return 11;
      case "Diciembre":
        return 12;
    }
  }

  public obtenerCatalogos(): void {
    this.catTipoCorredor$ = this.catalogoService
      .obtenerCatalogo<ElementoCatalogo>(RutaCatalogoEnum.CORREDORES)
      .pipe(take(1));
    this.tiposCarpetas$ = this.catalogoService
      .obtenerCatalogo<ElementoCatalogo>(RutaCatalogoEnum.CARPETAS)
      .pipe(take(1));
    this.tiposSecciones$ = this.catalogoService
      .obtenerCatalogo<ElementoCatalogo>(RutaCatalogoEnum.SECCIONES)
      .pipe(take(1));
    this.catTipoPrioridad$ = this.catalogoService
      .obtenerCatalogo<ElementoCatalogo>(RutaCatalogoEnum.PRIORIDAD)
      .pipe(take(1));
    this.catTipoOrigenRecursos$ = this.catalogoService
      .obtenerCatalogo<ElementoCatalogo>(RutaCatalogoEnum.ORIGEN_RECURSOS)
      .pipe(take(1));
  }

  private inicializarForm(): void {
    this.fichaTecnicaForm = this.formBuilder.group({
      keyLevantamientoObra: new FormControl(
        this.datosObra.obra.keyLevantamientoObra
      ),
      fechaActualizacion: new FormControl(this.date.getTime()),
      keyCatTipoPrioridad: new FormControl(
        this.datosObra.obra.keyCatTipoPrioridad,
        [Validators.required, this.validateId]
      ),
      keyCatOrigenRecursos: new FormControl(
        this.datosObra.obra.keyCatOrigenRecursos,
        [Validators.required, this.validateId]
      ),
      keyCatCorredor: new FormControl(this.datosObra.obra.keyCatCorredor, [
        Validators.required,
        this.validateId,
      ]),
      caracteristicas: new FormControl(this.datosObra.obra.caracteristicas, [
        Validators.required,
        Validators.maxLength(500),
      ]),
      tpda: new FormControl(this.datosObra.obra.tpda, [Validators.required]),
      keyCatCarpeta: new FormControl(this.datosObra.obra.keyCatCarpeta, [
        Validators.required,
        this.validateId,
      ]),
      keyCatSeccion: new FormControl(this.datosObra.obra.keyCatSeccion, [
        Validators.required,
        this.validateId,
      ]),
      // periodoEjecucion: new FormControl(this.datosObra.obra.periodoEjecucion, [
      //   Validators.required,
      // ]),
      inicioPeriodoEjecucion: new FormControl(this.periodoInicio),
      finPeriodoEjecucion: new FormControl(this.periodoFin),
      longitudTotal: new FormControl(this.datosObra.obra.longitudTotal, [
        Validators.required,
      ]),
      inversionTotal: new FormControl(this.datosObra.obra.inversionTotal, [
        Validators.required,
      ]),
      esquemaFinanciamiento: new FormControl(
        this.datosObra.obra.esquemaFinanciamiento,
        [Validators.required]
      ),
      registroSHCP: new FormControl(this.datosObra.obra.registroSHCP, [
        Validators.required,
      ]),
      estatusSHCP: new FormControl(this.datosObra.obra.estatusSHCP, [
        Validators.required,
      ]),
      proyectoEjecutivo: new FormControl(
        this.datosObra.obra.proyectoEjecutivo,
        [
          Validators.required,
          Validators.pattern("^((?:|0|[1-9]\\d?|100)(?:\\.\\d{1,2})?)$"),
        ]
      ),
      liberacionDerechoVia: new FormControl(
        this.datosObra.obra.liberacionDerechoVia,
        [
          Validators.required,
          Validators.pattern("^((?:|0|[1-9]\\d?|100)(?:\\.\\d{1,2})?)$"),
        ]
      ),
      cus: new FormControl(this.datosObra.obra.cus, [
        Validators.required,
        Validators.pattern("^((?:|0|[1-9]\\d?|100)(?:\\.\\d{1,2})?)$"),
      ]),
      mia: new FormControl(this.datosObra.obra.mia, [
        Validators.required,
        Validators.pattern("^((?:|0|[1-9]\\d?|100)(?:\\.\\d{1,2})?)$"),
      ]),
      avanceFisicoGlobal: new FormControl(
        this.datosObra.obra.avanceFisicoGlobal,
        [
          Validators.required,
          Validators.pattern("^((?:|0|[1-9]\\d?|100)(?:\\.\\d{1,2})?)$"),
        ]
      ),
      avanceFinancieroGlobal: new FormControl(
        this.datosObra.obra.avanceFinancieroGlobal,
        [
          Validators.required,
          Validators.pattern("^((?:|0|[1-9]\\d?|100)(?:\\.\\d{1,2})?)$"),
        ]
      ),
      avanceFisico: new FormControl(this.datosObra.obra.avanceFisico, [
        Validators.required,
        Validators.pattern("^((?:|0|[1-9]\\d?|100)(?:\\.\\d{1,2})?)$"),
      ]),
      avanceFinanciero: new FormControl(this.datosObra.obra.avanceFinanciero, [
        Validators.required,
      ]),
      observaciones: new FormControl(this.datosObra.obra.observaciones, [
        Validators.required,
        Validators.maxLength(500),
      ]),
      problematica: new FormControl(this.datosObra.obra.problematica, [
        Validators.required,
        Validators.maxLength(500),
      ]),
      beneficios: new FormControl(this.datosObra.obra.beneficios, [
        Validators.required,
        Validators.maxLength(500),
      ]),
      proximasAcciones: new FormControl(this.datosObra.obra.proximasAcciones, [
        Validators.required,
        Validators.maxLength(500),
      ]),
      incluyeImagenes: new FormControl(this.datosObra.obra.incluyeImagenes),
      keyUsuario: new FormControl(this.usuarioEnSesion.key),
      ejerciciosFiscales: this.formBuilder.array([], Validators.required),
    });
    this.generarArrayEjerciciosFiscales(this.datosObra.obra.ejerciciosFiscales);
    console.log(this.datosObra);
  }

  public validarCheck(checkValue: boolean) {
    if (!checkValue) {
      this.fichaTecnicaForm.get("incluyeImagenes").setValue(0);
    }
  }

  public crearFormEjercicioFiscal(): FormGroup {
    return this.formBuilder.group({
      letrasEsquemaFiscal: new FormControl(""),
      esquemaFiscal: new FormControl(""),
      letrasAsignacion: new FormControl(""),
      asignacion: new FormControl(""),
      letrasMeta: new FormControl(""),
      meta: new FormControl(""),
    });
  }

  public generarArrayEjerciciosFiscales(ejerciciosFiscales: EjercicioFiscal[]) {
    ejerciciosFiscales.forEach((ejercicioFiscal) => {
      (this.fichaTecnicaForm.get("ejerciciosFiscales") as FormArray).push(
        this.formArrayEjercicioFiscal(ejercicioFiscal)
      );
    });
  }

  public formArrayEjercicioFiscal(ejercicioFiscal: EjercicioFiscal): FormGroup {
    return this.formBuilder.group({
      letrasEsquemaFiscal: new FormControl(ejercicioFiscal.letrasEsquemaFiscal),
      esquemaFiscal: new FormControl(ejercicioFiscal.esquemaFiscal),
      letrasAsignacion: new FormControl(ejercicioFiscal.letrasAsignacion),
      asignacion: new FormControl(ejercicioFiscal.asignacion),
      letrasMeta: new FormControl(ejercicioFiscal.letrasMeta),
      meta: new FormControl(ejercicioFiscal.meta),
    });
  }

  // public cargarMapa() {
  //   this.urlIframe = this.sanitizer.bypassSecurityTrustResourceUrl("https://sctsupervision-1527449638423.firebaseapp.com/Supervision1.0/Mapa_01.html?a="+this.estadoIframe+"&b="+this.obraIframe+"&c="+this.licitacionIframe+"&d="+this.fechaIframe);
  // }

  public agregarEjercicioFiscal(): void {
    (this.fichaTecnicaForm.get("ejerciciosFiscales") as FormArray).push(
      this.crearFormEjercicioFiscal()
    );
  }

  public eliminarEjercicioFiscal(indice: number): void {
    (<FormArray>this.fichaTecnicaForm.controls.ejerciciosFiscales).removeAt(
      indice
    );
    console.log(this.datosObra.obra.ejerciciosFiscales[indice]);
  }

  formatDate(date) {
    let d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    switch (month) {
      case "01":
        month = "Enero";
        break;
      case "02":
        month = "Febrero";
        break;
      case "03":
        month = "Marzo";
        break;
      case "04":
        month = "Abril";
        break;
      case "05":
        month = "Mayo";
      case "06":
        month = "Junio";
      case "07":
        month = "Julio";
      case "08":
        month = "Agosto";
        break;
      case "09":
        month = "Septiembre";
        break;
      case "10":
        month = "Octubre";
        break;
      case "11":
        month = "Noviembre";
        break;
      case "12":
        month = "Diciembre";
        break;
    }

    return [month, year].join(" ");
  }

  public async actualizarFichaTecnica() {
    let obraHistorico: ObraHistorico = this.crearModeloHistorico();
    await this.obrasHistoricoService.save(obraHistorico);
    this.updateFichaTecnica();

    // this.router.navigate(['/tablero/consultar-obras'], { skipLocationChange: true });
  }
  // TODO: función para actualizar ficha tecnica en ambas bases de datos;
  updateFichaTecnica() {
    // Procesamiento de fecha periodo de ejecución
    this.alert.cargando();
    const { inicioPeriodoEjecucion, finPeriodoEjecucion, ...form } =
      this.fichaTecnicaForm.value;
    const periodoEjecucion = `${this.formatDate(
      inicioPeriodoEjecucion
    )} - ${this.formatDate(finPeriodoEjecucion)}`;
    const newForm = { periodoEjecucion, ...form };
    // Aqui termina el proceso de fecha periodo de ejecucion

    let obraActualizada: Obra = this.crearModelo(newForm);
    this.obraService.update(this.keyObra, obraActualizada).then(() => {
      this.alertasService.mostrarAlerta(
        "success",
        "Obra actualizada exitosamente."
      );
      console.log("Obra actualizada correctamente en firebase.");
    });
    // this.actualizarFichaTenicaEnServidor(obraActualizada);
  }

  actualizarFichaTenicaEnServidor(obraActualizada) {
    this.levantamientoObraService
      .findByKey(obraActualizada.keyLevantamientoObra)
      .subscribe((obraLevantamiento: LevantamientoObra) => {
        const obraCompleta = {
          nombreObra: obraLevantamiento.nombreObra,
          nombreTramoLicitacion: obraLevantamiento.nombreTramoLicitacion,
          latitud: obraLevantamiento.latitud,
          longitud: obraLevantamiento.longitud,
          nombreEstado: this.catalogoEstados.find(
            (estados: Estado) => estados.key === obraLevantamiento.keyCatEstado
          ).nombre,
          ...obraActualizada,
        };
        // Guardando obraCompleta en el servidor SQL
        this.obraService.updateFicha(obraCompleta).subscribe(
          (data) => {
            console.log("Obra actualizada correctamente en el servidor SQL.");
          },
          (err) => {
            console.log("Hubo un error! Al insertar en el servidor SQL.");
          }
        );
      });
  }

  public crearModeloHistorico(): ObraHistorico {
    let obraHistorico: ObraHistorico = Object.assign(
      {},
      this.datosObra.obra as ObraHistorico
    );
    obraHistorico.fechaActualizacion = this.fechaActual;
    obraHistorico.keyUsuarioActualizacion = this.usuarioEnSesion.key;
    return obraHistorico;
  }

  public crearModelo(fichaTecnicaDeForm: any): Obra {
    let obra: Obra = Object.assign({}, fichaTecnicaDeForm as Obra);
    return obra;
  }

  private validateId(control: AbstractControl) {
    return control.value === "-1" ? { error: "El campo es requerido" } : null;
  }

  public getError(controlName: string): string {
    let error = "";
    const control = this.fichaTecnicaForm.get(controlName);
    if (control.errors != null) {
      error = "El campo es requerido";
    }
    return error;
  }

  public ngOnDestroy(): void {
    if (this.suscripcionObra) {
      this.suscripcionObra.unsubscribe();
    }
  }
}
